import { FC } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown } from "capitalrx-components-2.0/lib/forms";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";

import { Prices } from "@/App/api/drug";
import { Pharmacy } from "@/App/api/pharmacy";
import { PharmacyTile, ThemedButton } from "@/App/components";
import { TextDropdown } from "@/App/components/themed";

import { SortSvg } from "./SortSvg";

const SORT_OPTIONS = ["distance", "low_to_high"] as const;

const TRANSLATION_PATH = "FIND_BEST_PRICE.findBestPriceResultsSection";

type SortValues = (typeof SORT_OPTIONS)[number];

interface FindBestPriceResultsSectionProps {
  readonly sectionTitle: JSX.Element;
  readonly prices: Prices[];
  readonly isLoading: boolean;
  readonly onDrugSelected: (prices: Prices) => void;
  readonly getDrugPriceDisplay: (prices: Prices) => string;
  readonly sortBy?: string;
  readonly setSortBy?: (sortBy: SortValues) => void;
  readonly pharmacyDescription?: string;
  readonly userLocation?: { readonly latitude: string; readonly longitude: string };
  readonly hideDisplayTags?: boolean;
  readonly hideIndex?: boolean;
  readonly pageNumber?: number;
  readonly firstPageResultCount?: number;
  readonly resultsPerPage?: number;
}

export const FindBestPriceResultsSection: FC<FindBestPriceResultsSectionProps> = ({
  sectionTitle,
  prices,
  isLoading,
  onDrugSelected,
  getDrugPriceDisplay,
  sortBy,
  setSortBy,
  pharmacyDescription,
  userLocation,
  hideDisplayTags,
  hideIndex,
  pageNumber,
  firstPageResultCount = 6,
  resultsPerPage = 6,
}) => {
  const { t: translate } = useTranslation();

  const { isCompressed, noPricingStyle } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
    noPricingStyle: {
      desktop: {},
      mobile: {},
      tablet: {
        flex: "none",
        width: "auto",
        maxWidth: "8rem",
      },
    },
  });

  const usingSortDropdown = !!setSortBy;

  let firstPageCountDelta = 0;

  if (firstPageResultCount && pageNumber !== 1) {
    firstPageCountDelta = (resultsPerPage - firstPageResultCount);
  }

  return (
    <Stack>
      {isCompressed && (
        <>
          {usingSortDropdown && (
            <Stack>
              {sectionTitle}
              <Shelf>
                <Shelf.Spacer />
                <TextDropdown prefix={<SortSvg />} value={sortBy || "distance"} onChange={(value) => setSortBy(value as SortValues)} hideLabel>
                  {SORT_OPTIONS.map((option) => (
                    <Dropdown.Option value={option} key={option}>
                      {translate(`${TRANSLATION_PATH}.${option}`)}
                    </Dropdown.Option>
                  ))}
                </TextDropdown>
                <Shelf.Spacer />
              </Shelf>
            </Stack>
          )}
          {!usingSortDropdown && sectionTitle}
        </>
      )}
      {!isCompressed && (
        <>
          {usingSortDropdown && (
            <Stack spacing="small">
              <Subheading>{sectionTitle}</Subheading>
              <Shelf>
                <TextDropdown prefix={<SortSvg />} value={sortBy || "distance"} onChange={(value) => setSortBy(value as SortValues)} hideLabel>
                  {SORT_OPTIONS.map((option) => (
                    <Dropdown.Option value={option} key={option}>
                      {translate(`${TRANSLATION_PATH}.${option}`)}
                    </Dropdown.Option>
                  ))}
                </TextDropdown>
                <Shelf.Spacer />
                <Semibold>{translate(`${TRANSLATION_PATH}.estimatedAmountYouWillPay`)}</Semibold>
              </Shelf>
            </Stack>
          )}
          {!usingSortDropdown && (
            <Shelf>
              <Subheading>{sectionTitle}</Subheading>
              <Shelf.Spacer />
              <Semibold>{translate(`${TRANSLATION_PATH}.estimatedAmountYouWillPay`)}</Semibold>
            </Shelf>
          )}
        </>
      )}
      <Stack testId="pharmacy-pricings-container">
        {prices?.map((drugPrice, idx) => {
          let rowIdx = idx;

          if (!hideIndex && pageNumber !== null) {
            rowIdx = idx + resultsPerPage * (pageNumber - 1) - firstPageCountDelta;
          }

          return (
            <PharmacyTile
              key={drugPrice.pharmacy.provider_id}
              pharmacy={drugPrice.pharmacy as unknown as Pharmacy}
              pharmacyDescription={pharmacyDescription}
              userLocation={userLocation}
              isLoading={isLoading}
              hideDisplayTags={hideDisplayTags}
              index={hideIndex ? null : rowIdx}
              button={
                <>
                  {drugPrice.claim.transmission.claim_status === "P" && (
                    <Stack spacing="small">
                      {isCompressed && (
                        <Semibold style={{ color: "#313E48" }}>
                          {translate(`${TRANSLATION_PATH}.estimatedAmountYouWillPay`)}
                        </Semibold>
                      )}
                      <ThemedButton
                        testId={`go-to-pharmacy-${drugPrice.pharmacy.provider_id}-pricing`}
                        onClick={() => onDrugSelected(drugPrice)}
                        icon="arrow-right"
                        iconPosition="right"
                        appearance="secondary"
                      >
                        <Stack alignment="center">
                          {getDrugPriceDisplay(drugPrice)}
                        </Stack>
                      </ThemedButton>
                    </Stack>
                  )}
                  {drugPrice.claim.transmission.claim_status !== "P" && (
                    <Body textAlign={isCompressed ? "center" : "left"} style={noPricingStyle}>
                      {translate(`${TRANSLATION_PATH}.pricingOrFulfillmentUnavailable`)}
                    </Body>
                  )}
                </>
              }
            />
          );
        })}
      </Stack>
    </Stack>
  );
};
